import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getCompanyByIdClient } from '../../../api/redux/slices/Customer';
import { LoaderComponent } from '../../../helpers/components/loader';
import defau from '../../../assets/default.png';

import {
	BsBuilding,
	BsTelephone,
	BsEnvelope,
	BsGeoAlt,
	BsCardText,
	BsCalendar,
	BsNewspaper,
	BsAward,
	BsX,
	BsArrowRight,
	BsGlobe,
	BsPeople,
	BsTag,
	BsInfoCircle,
} from 'react-icons/bs';
import './CompanyProfileClient.scss';

// Mapping des catégories d'économie
const economyLabels = {
	'economie_vert': '🌱 Économie verte',
	'economie_sociale': '🤝 Économie sociale',
	'economie_circulaire': '🔄 Économie circulaire',
	'economie_numerique': '💻 Économie numérique',
	'economie_bleue': '🌊 Économie bleue',
};

// Mapping des types de société
const companyTypeLabels = {
	'SARL': 'SARL',
	'SUARL': 'SUARL',
	'SA': 'SA',
	'SAS': 'SAS',
	'SNC': 'SNC',
	'SCS': 'SCS',
	'SCA': 'SCA',
	'EURL': 'EURL',
	'Entreprise_Individuelle': 'Entreprise Individuelle',
	'Autre': 'Autre',
};

// ─── Modal ────────────────────────────────────────────────────────────────────
const ActualityModal = ({ actuality, onClose }) => {
	useEffect(() => {
		const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
		document.addEventListener('keydown', handleKey);
		document.body.style.overflow = 'hidden';
		return () => {
			document.removeEventListener('keydown', handleKey);
			document.body.style.overflow = '';
		};
	}, [onClose]);

	return (
		<div className='modal-backdrop' onClick={(e) => e.target === e.currentTarget && onClose()}>
			<div className='modal-box'>
				<button className='modal-close' onClick={onClose}>
					<BsX />
				</button>

				<div className='modal-image'>
					<img 
						src={actuality.image?.secure_url || defau} 
						alt={actuality.title} 
					/>
					{actuality.date && (
						<span className='modal-date-badge'>
							{new Date(actuality.date).toLocaleDateString('fr-FR')}
						</span>
					)}
				</div>

				<div className='modal-body'>
					<h2>{actuality.title}</h2>
					<p>{actuality.description}</p>
				</div>
			</div>
		</div>
	);
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const CompanyProfileClient = () => {
	const dispatch = useDispatch();
	const { id } = useParams();
	const token = localStorage.getItem('accesstoken');
	const { companyDetails, loading } = useSelector((state) => state.customer);
	const [company, setCompany] = useState(null);
	const [selectedActuality, setSelectedActuality] = useState(null);

	useEffect(() => {
		if (token && id) dispatch(getCompanyByIdClient({ token, id }));
	}, [dispatch, token, id]);

	useEffect(() => {
		if (companyDetails) setCompany(companyDetails);
	}, [companyDetails]);

	// Helper function to display value or "-"
	const displayValue = (value) => {
		if (value === null || value === undefined || value === '') {
			return <span className='empty-value'>-</span>;
		}
		return value;
	};

	if (loading) return <LoaderComponent />;

	if (!company) {
		return (
			<div className='company-profile-page'>
				<div className='company-not-found'>
					<BsBuilding />
					<h2>Société non trouvée</h2>
					<p>La société que vous recherchez n'existe pas ou a été supprimée.</p>
				</div>
			</div>
		);
	}

	const hasActualities = company.actualites?.length > 0;
	const hasCertificates = company.certificats?.length > 0;
	const hasEconomyCategories = company.categories_economie?.length > 0;
	const hasCompanyType = company.company_type && company.company_type !== '';
	
	const truncate = (text = '', max = 100) =>
		text.length > max ? text.slice(0, max).trimEnd() + '...' : text;

	const getCompanyTypeDisplay = (type) => {
		return companyTypeLabels[type] || type || 'Non spécifié';
	};

	return (
		<div className='company-profile-page'>

			{selectedActuality && (
				<ActualityModal
					actuality={selectedActuality}
					onClose={() => setSelectedActuality(null)}
				/>
			)}

			{/* ── Hero */}
			<div className='profile-hero'>
				<div className='company-logo'>
					{company.image?.secure_url ? (
						<img src={company.image.secure_url} alt={company.name} />
					) : (
						<div className='logo-placeholder'>
							<BsBuilding />
						</div>
					)}
				</div>
				<div className='company-title'>
					<h1>{company.name}</h1>
					{company.date_creation_societe && (
						<div className='creation-date'>
							<BsCalendar />
							<span>
								Créée le {new Date(company.date_creation_societe).toLocaleDateString('fr-FR')}
							</span>
						</div>
					)}
				</div>
			</div>

			<div className='profile-content'>

				{/* ── Informations */}
				<section className='info-section'>
					<h2 className='section-title'><BsCardText /> Informations</h2>
					<div className='info-grid'>
						{/* Company Type - Now as an info card */}
						<div className='info-card'>
							<div className='card-icon'><BsInfoCircle /></div>
							<div className='card-content'>
								<h4>Type de société</h4>
								<p>{hasCompanyType ? getCompanyTypeDisplay(company.company_type) : <span className='empty-value'>-</span>}</p>
							</div>
						</div>
						<div className='info-card'>
							<div className='card-icon'><BsGeoAlt /></div>
							<div className='card-content'>
								<h4>Adresse</h4>
								<p>{displayValue(company.address)}</p>
							</div>
						</div>
						<div className='info-card'>
							<div className='card-icon'><BsTelephone /></div>
							<div className='card-content'>
								<h4>Téléphone</h4>
								<p>{displayValue(company.phone)}</p>
							</div>
						</div>
						<div className='info-card'>
							<div className='card-icon'><BsCardText /></div>
							<div className='card-content'>
								<h4>Fax</h4>
								<p>{displayValue(company.fax)}</p>
							</div>
						</div>
						<div className='info-card'>
							<div className='card-icon'><BsEnvelope /></div>
							<div className='card-content'>
								<h4>Email</h4>
								<p>{displayValue(company.email)}</p>
							</div>
						</div>
						<div className='info-card'>
							<div className='card-icon'><BsGlobe /></div>
							<div className='card-content'>
								<h4>Site Web</h4>
								{company.siteweb ? (
									<a
										href={company.siteweb.startsWith('http') ? company.siteweb : `https://${company.siteweb}`}
										target='_blank'
										rel='noreferrer'
									>
										{company.siteweb}
									</a>
								) : (
									<span className='empty-value'>-</span>
								)}
							</div>
						</div>
						<div className='info-card'>
							<div className='card-icon'><BsPeople /></div>
							<div className='card-content'>
								<h4>Effectif</h4>
								<p>{company.nombre_employes > 0 ? `${company.nombre_employes} employés` : <span className='empty-value'>-</span>}</p>
							</div>
						</div>
					</div>

					{/* Catégories d'économie */}
					<div className='economy-section'>
						<h2 className='section-title'><BsTag /> Domaines d'activité</h2>
						{hasEconomyCategories ? (
							<div className='economy-badges'>
								{company.categories_economie.map((category, index) => (
									<span key={index} className='economy-badge'>
										{economyLabels[category] || category}
									</span>
								))}
							</div>
						) : (
							<span className='empty-value'>-</span>
						)}
					</div>

					{/* Informations légales */}
					<div className='codes-section'>
						<h2 className='section-title'><BsCardText /> Informations légales</h2>
						<div className='codes-grid'>
							<div className='code-card'>
								<span className='code-label'>Code TVA</span>
								<span className='code-value'>{displayValue(company.tva_code)}</span>
							</div>
							<div className='code-card'>
								<span className='code-label'>Registre de commerce</span>
								<span className='code-value'>{displayValue(company.r_c)}</span>
							</div>
							<div className='code-card'>
								<span className='code-label'>Code douane</span>
								<span className='code-value'>{displayValue(company.customs_code)}</span>
							</div>
						</div>
					</div>
				</section>

				{/* ── Actualités */}
				<section className='actualites-section'>
					<h2 className='section-title'>
						<BsNewspaper /> Actualités {hasActualities && `(${company.actualites.length})`}
					</h2>
					{hasActualities ? (
						<div className='actualites-grid'>
							{company.actualites.map((actuality) => (
								<div key={actuality._id} className='actuality-card'>
									<div className='actuality-image'>
										<img 
											src={actuality.image?.secure_url || defau} 
											alt={actuality.title} 
										/>
										{actuality.date && (
											<span className='date-badge'>
												{new Date(actuality.date).toLocaleDateString('fr-FR')}
											</span>
										)}
									</div>
									<div className='actuality-content'>
										<h3>{actuality.title}</h3>
										<p>{truncate(actuality.description, 100)}</p>
										<button
											className='btn-read-more'
											onClick={() => setSelectedActuality(actuality)}
										>
											<span>Lire la suite</span>
											<BsArrowRight />
										</button>
									</div>
								</div>
							))}
						</div>
					) : (
						<div className='empty-state'>
							<BsNewspaper />
							<p>Aucune actualité pour le moment</p>
						</div>
					)}
				</section>

				{/* ── Certificats */}
				<section className='certificats-section'>
					<h2 className='section-title'>
						<BsAward /> Certificats {hasCertificates && `(${company.certificats.length})`}
					</h2>
					{hasCertificates ? (
						<div className='certificats-grid'>
							{company.certificats.map((certificate) => (
								<div key={certificate._id} className='certificate-card'>
									{certificate.image?.secure_url && (
										<div className='certificate-image'>
											<img src={certificate.image.secure_url} alt={certificate.name} />
											<div className='cert-overlay'><span>Voir le certificat</span></div>
										</div>
									)}
									<div className='certificate-content'>
										<BsAward />
										<h4>{certificate.name}</h4>
									</div>
								</div>
							))}
						</div>
					) : (
						<div className='empty-state'>
							<BsAward />
							<p>Aucun certificat pour le moment</p>
						</div>
					)}
				</section>

			</div>
		</div>
	);
};

export default CompanyProfileClient;